import { Star } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import {
    alpha,
    Box,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ImageSlider from '../../../layout/styledcomponents/CustomImageSlider/ImageSlider';
import { capitalizeName, formatCurrency } from '../../../shared/utils';
import { ExperienceType } from '../ExperienceReviews/ReviewWizard';
import { trackEvent } from '../../../app/Analytics';
import {
    useAddToWishlistMutation,
    useRemoveFromWishlistMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import UserRole from '../../../shared/AuthRoles';
import toast from '../../shared/Toast';
import { formatHireRange } from './IndividualExperienceDetails';

type DiscountDateRange = {
    startDate: string | Date;
    endDate: string | Date;
};

type Discount = {
    percentage: number;
    disabled?: boolean;
    expired?: boolean;
    dates: DiscountDateRange[];
};

type Experience = {
    discounts?: Discount[];
};

type DiscountBadgeLabel = { label: string };

const getFirstActiveDiscount = (
    experience: Experience,
): DiscountBadgeLabel | null => {
    const today = new Date();

    const validDiscounts = (experience?.discounts || [])
        .filter(
            (discount) =>
                !discount.disabled &&
                !discount.expired &&
                Array.isArray(discount.dates) &&
                discount.dates.length > 0,
        )
        .flatMap((discount) =>
            discount.dates
                .map((range) => {
                    const start = new Date(range.startDate);
                    const end = new Date(range.endDate);
                    return {
                        percentage: discount.percentage,
                        start,
                        end,
                    };
                })
                .filter(({ end }) => end >= today),
        )
        .sort((a, b) => a.start.getTime() - b.start.getTime());

    if (validDiscounts.length === 0) return null;

    const currentMonth = validDiscounts.find((d) => {
        const s = d.start;
        return (
            s.getMonth() === today.getMonth() &&
            s.getFullYear() === today.getFullYear()
        );
    });

    if (currentMonth) {
        return { label: `${currentMonth.percentage}% discount this month` };
    }

    if (validDiscounts.length === 1) {
        const next = validDiscounts[0];
        return {
            label: `${next.percentage}% discount \nin ${next.start.toLocaleString(
                'default',
                {
                    month: 'short',
                    year: 'numeric',
                },
            )}`,
        };
    }

    return { label: 'Available discounts' };
};

export const isNewExperience = (dateAdded: any) => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return new Date(dateAdded) >= thirtyDaysAgo;
};

const formatUnitType = (unitType: string) => {
    const typeMap: Record<string, string> = {
        fixed: 'Fixed price',
        perHour: 'per hour',
        perDay: 'per day',
        perNight: 'per night',
        perHourPerGuest: 'per hour per guest',
        perDayPerGuest: 'per day per guest',
        perNightPerGuest: 'per night per guest',
        perDeskPerDay: 'per day',
        perOfficePerDay: 'per day',
        perMeetingRoomPerDay: 'per day',
        entireVenuePerDay: 'per day',
        entireVenuePerNight: 'per night',
    };

    return typeMap[unitType] || unitType;
};

export const getPriceRange = (experience: any) => {
    if (experience?.billingExcluded) {
        return {
            minPrice: 0,
            maxPrice: 0,
            unitType: null,
            discountPercentage: 0,
            billingExcluded: true,
        };
    }

    const charges = experience?.charges || [];
    const today = new Date();

    const activeDiscount = experience?.discounts?.find(
        (discount: any) =>
            !discount.disabled &&
            !discount.expired &&
            Array.isArray(discount.dates) &&
            discount.dates.some((range: any) => {
                const start = new Date(range.startDate);
                const end = new Date(range.endDate);
                return today >= start && today <= end;
            }),
    );

    const discountPercentage = activeDiscount?.percentage ?? 0;

    const priceCharges = charges.filter(
        (p: any) => p.chargeType === 'price' && typeof p.unitPrice === 'number',
    );

    if (priceCharges.length === 0) {
        return {
            minPrice: 0,
            maxPrice: 0,
            unitType: null,
            discountPercentage,
            billingExcluded: false,
        };
    }

    const sorted = [...priceCharges].sort((a, b) => a.unitPrice - b.unitPrice);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];

    const minPrice = min.unitPrice * (1 - discountPercentage / 100);
    const maxPrice = max.unitPrice * (1 - discountPercentage / 100);

    return {
        minPrice: Math.round(minPrice),
        maxPrice: Math.round(maxPrice),
        unitType: max.type,
        discountPercentage,
        billingExcluded: false,
    };
};

const getExperienceCapacityText = (experience: any): string | null => {
    const { type, details } = experience ?? {};

    if (type === ExperienceType.Vehicle) {
        const seats = details?.numberOfSeats;
        return seats != null ? `${seats} Seats` : null;
    }

    if (type === ExperienceType.Activity) {
        const quantity = details?.quantity;
        return quantity != null ? `${quantity} Available` : null;
    }

    const maxGuests = details?.maxGuests;
    return maxGuests != null ? `${maxGuests} Guests` : null;
};

const ExperienceCard = ({
    experience,
    component,
    visitType,
    isSmallChat,
    isItineraryItemForm,
    isWishlisted = false,
    onRemoveFromWishlist,
}: {
    experience: any;
    component?: string;
    visitType: string;
    isSmallChat?: boolean;
    isItineraryItemForm?: boolean;
    isWishlisted?: boolean;
    onRemoveFromWishlist?: (experienceId: number) => void;
}) => {
    const navigate = useNavigate();
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 770px)');
    const badgeSpaceOne = useMediaQuery(
        '(min-width: 600px) and (max-width: 770px)',
    );
    const badgeSpaceTwo = useMediaQuery('(min-width: 1200px');
    const loggedInUser = UserRole();
    const isGuestUser = loggedInUser?.role?.includes('guest');
    const [addToWishlist, { isLoading: isAddingToWishlist }] =
        useAddToWishlistMutation();
    const [removeFromWishlist, { isLoading: isRemovingFromWishlist }] =
        useRemoveFromWishlistMutation();

    const handleWishlistToggle = async (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        event.preventDefault();
        event.stopPropagation();
        if (!experience?.id) {
            return;
        }

        try {
            if (isWishlisted) {
                await removeFromWishlist(experience.id).unwrap();
                onRemoveFromWishlist?.(experience.id);
                return;
            }
            await addToWishlist(experience.id).unwrap();
        } catch {
            toast.error('Failed to add to wishlist');
        }
    };

    const handleOpen = (experience: any) => {
        if (isItineraryItemForm) {
            return;
        }
        const url = `/viewExperiences/${experience?.type.toLowerCase()}/${
            experience?.id
        }`;

        if (component === 'aiGuestReservation') {
            window.open(url, '_blank');
        } else {
            navigate(url, {
                state: { experience, component: 'all' },
            });
        }

        trackEvent(
            'Experiences',
            `Individual Experience (${visitType})`,
            `Ind. Experience Clicked (${visitType})`,
        );
    };

    const theme = useTheme();
    const { minPrice, maxPrice, unitType, billingExcluded } =
        getPriceRange(experience);
    const capacityText = getExperienceCapacityText(experience);
    return (
        <Box
            sx={{
                textDecoration: 'none',
                height: '100%',
            }}
        >
            <Card
                onClick={() => handleOpen(experience)}
                variant='outlined'
                sx={{
                    position: 'relative',
                    maxWidth: 500,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'white.main',
                    borderStyle: 'none',
                    borderRadius: '8px',
                    boxShadow: `5px 8px 15px 0px ${alpha(theme.palette.dark.main, 0.08)}, 0px 0px 20px 0px ${alpha(theme.palette.dark.main, 0.08)}`,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    height: '100%',
                    '&:after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(146, 144, 144, 0.1)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: 'none',
                    },
                    '&:hover': { '&:after': { opacity: 1 } },
                }}
            >
                <CardActionArea
                    disableRipple
                    disableTouchRipple
                    focusRipple={false}
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '1 / 1',
                        }}
                    >
                        <ImageSlider
                            images={experience.photos}
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                overflow: 'hidden',
                            }}
                        />
                        {isGuestUser && (
                            <IconButton
                                size='small'
                                aria-label={
                                    isWishlisted
                                        ? 'Remove from favourites'
                                        : 'Add to favourites'
                                }
                                onClick={handleWishlistToggle}
                                sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    left: 8,
                                    zIndex: 11,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: 22,
                                        height: 22,
                                    }}
                                >
                                    {isWishlisted && (
                                        <FavoriteIcon
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                fontSize: 22,
                                                color: 'red.main',
                                            }}
                                        />
                                    )}
                                    <FavoriteBorderIcon
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            fontSize: 22,
                                            color: (theme) =>
                                                alpha(
                                                    theme.palette.common.white,
                                                    0.9,
                                                ),
                                        }}
                                    />
                                </Box>
                            </IconButton>
                        )}
                        {isNewExperience(experience.createdAt) && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: 50,
                                    height: 50,
                                    background: `linear-gradient(225deg, ${theme.palette.secondary.main} 50%, transparent 50%)`,
                                }}
                            >
                                {isNewExperience(experience.createdAt) && (
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            display: 'inline-block',
                                            textTransform: 'uppercase',
                                            transform: 'rotate(45deg)',
                                            fontWeight: '600',
                                            color: 'neutral.main',
                                            pl: 2.5,
                                            pb: 0.5,
                                        }}
                                    >
                                        New
                                    </Typography>
                                )}
                            </Box>
                        )}
                        {getFirstActiveDiscount(experience) && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '19px',
                                    right: '-32px',
                                    borderBottom: `33px solid ${theme.palette.green.main}`,
                                    borderLeft: '33px solid transparent',
                                    borderRight: '33px solid transparent',
                                    boxShadow: `0px 10px 40px 0px ${alpha(theme.palette.dark.main, 0.1)}`,
                                    height: '0px',
                                    width: '136px',
                                    transform: 'rotate(45deg)',
                                }}
                            >
                                {(() => {
                                    const discount =
                                        getFirstActiveDiscount(experience);
                                    return (
                                        discount && (
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    display: 'inline-block',
                                                    fontWeight: '600',
                                                    lineHeight: '.9',
                                                    color: 'neutral.main',
                                                    width: '90px',
                                                    mt: '6px',
                                                    ml: '-5px',
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {discount.label}
                                            </Typography>
                                        )
                                    );
                                })()}
                            </Box>
                        )}
                    </Box>
                    <CardContent
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            padding: isSmallChat ? 1.2 : 2,
                            paddingBottom: 1.2,
                            width: '100%',
                            gap: 1,
                            minHeight: {
                                xs: 100,
                                sm: isItineraryItemForm ? 120 : 100,
                                lg: 100,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: isItineraryItemForm ? 'flex' : 'none',
                                flexDirection: 'column',
                                gap: isItineraryItemForm ? 0 : 0.5,
                            }}
                        >
                            <Typography
                                variant='subHeading'
                                component={'h3'}
                                noWrap
                                sx={{
                                    fontSize: isItineraryItemForm
                                        ? '14px'
                                        : isIpad
                                          ? '16px'
                                          : '',
                                    fontWeight: '600',
                                }}
                            >
                                {capitalizeName(experience.name)}
                            </Typography>
                            <Typography
                                component={'span'}
                                variant='h6'
                                sx={{
                                    fontWeight: isItineraryItemForm
                                        ? '400'
                                        : '600',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '12ch',
                                    lineHeight: '2.25',
                                }}
                            >
                                {capitalizeName(experience?.suburb)},{' '}
                                {capitalizeName(experience?.state)}
                            </Typography>
                            {!isItineraryItemForm && (
                                <Typography component='span' variant='h6'>
                                    {capitalizeName(
                                        experience?.type === 'commercial' &&
                                            experience?.details?.subType ===
                                                'house'
                                            ? 'home office'
                                            : experience?.details?.subType,
                                    )}
                                </Typography>
                            )}
                            {capacityText && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        color: 'text.main',
                                        fontSize: '12px',
                                    }}
                                >
                                    <PeopleOutlineIcon
                                        sx={{
                                            fontSize: '16px',
                                            mr: 0.5,
                                            position: 'relative',
                                            bottom: '2px',
                                        }}
                                    />
                                    {capacityText}
                                </Box>
                            )}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    color: 'text.main',
                                    fontSize: '12px',
                                }}
                            >
                                <AccessTimeIcon
                                    sx={{
                                        fontSize: '16px',
                                        mr: 0.5,
                                        position: 'relative',
                                        bottom: '2px',
                                    }}
                                />
                                {formatHireRange(experience)}
                            </Box>
                            <Typography
                                variant='body3'
                                sx={{
                                    fontWeight: billingExcluded
                                        ? 'normal'
                                        : '700',
                                    fontSize: '.65rem',
                                    mt: 1,
                                    marginLeft:
                                        (badgeSpaceOne || badgeSpaceTwo) &&
                                        !isItineraryItemForm
                                            ? '16px'
                                            : '0',
                                }}
                            >
                                {billingExcluded
                                    ? 'Free'
                                    : minPrice === 0 && maxPrice === 0
                                      ? ''
                                      : `From ${formatCurrency(minPrice)}$` +
                                        (unitType
                                            ? ` / ${formatUnitType(unitType)}`
                                            : '')}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: isItineraryItemForm ? 'none' : 'flex',
                                flexDirection: 'column',
                                gap: isSmallChat ? 0.5 : 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: isSmallChat
                                        ? 'column'
                                        : 'row',
                                    justifyContent: 'space-between',
                                    gap: isSmallChat ? 0.5 : 1,
                                }}
                            >
                                <Typography
                                    component={'h3'}
                                    variant='h5'
                                    noWrap
                                    sx={{
                                        fontSize: isSmallChat
                                            ? '14px'
                                            : '1.1rem',
                                    }}
                                >
                                    {capitalizeName(experience.name)}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: isSmallChat
                                            ? 'column'
                                            : 'row',
                                        justifyContent: 'space-between',
                                        alignItems: isSmallChat
                                            ? 'flex-start'
                                            : 'flex-end',
                                        gap: 0.5,
                                    }}
                                >
                                    {capacityText && (
                                        <Typography
                                            variant='body3'
                                            sx={{
                                                fontSize: isSmallChat
                                                    ? '10px'
                                                    : '12px',
                                            }}
                                        >
                                            <PeopleOutlineIcon
                                                sx={{
                                                    fontSize: '16px',
                                                    mr: 0.5,
                                                    position: 'relative',
                                                    bottom: '2px',
                                                }}
                                            />
                                            {capacityText}
                                        </Typography>
                                    )}

                                    {experience?.rating && (
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            {' '}
                                            <Typography
                                                variant='body3'
                                                sx={{
                                                    fontSize: '12px',
                                                }}
                                            >
                                                <Star
                                                    sx={{
                                                        fontSize: '16px',
                                                        mr: 0.5,

                                                        position: 'relative',
                                                        bottom: '1px',
                                                        color: 'secondary.main',
                                                    }}
                                                />

                                                {Number(
                                                    experience.rating,
                                                ).toFixed(1)}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Typography
                                        component='span'
                                        variant='h6'
                                        sx={{
                                            fontSize: isSmallChat
                                                ? '.7rem'
                                                : '.8rem',
                                            fontWeight: isSmallChat
                                                ? '700'
                                                : '600',
                                            whiteSpace: 'nowrap',
                                            position: 'absolute',
                                            top: 16,
                                            left: 0,
                                            px: 1,
                                            py: 0.25,
                                            borderTopRightRadius: 8,
                                            borderBottomRightRadius: 8,
                                            backgroundColor: alpha(
                                                theme.palette.neutral.main,
                                                0.7,
                                            ),
                                        }}
                                    >
                                        {capitalizeName(
                                            experience?.type === 'commercial' &&
                                                experience?.details?.subType ===
                                                    'house'
                                                ? 'home office'
                                                : experience?.details?.subType,
                                        )}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 0.5,
                                        flexDirection: isSmallChat
                                            ? 'column'
                                            : 'row',
                                        alignItems: isSmallChat
                                            ? 'flex-start'
                                            : 'flex-end',
                                    }}
                                >
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            fontSize: isSmallChat
                                                ? '10px'
                                                : '12px',
                                        }}
                                    >
                                        <AccessTimeIcon
                                            sx={{
                                                fontSize: '1rem',
                                                mr: 0.5,
                                                position: 'relative',
                                                bottom: '2px',
                                            }}
                                        />
                                        {formatHireRange(experience)}
                                    </Typography>
                                    <Typography
                                        variant='body3'
                                        sx={{
                                            fontWeight: '600',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '12ch',
                                        }}
                                    >
                                        {capitalizeName(experience?.suburb)},{' '}
                                        {capitalizeName(experience?.state)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent:
                                        isIpad && !isMobile
                                            ? 'flex-start'
                                            : 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        display:
                                            isIpad && !isMobile
                                                ? 'none'
                                                : 'flex',
                                        flex: 1,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 112,
                                        right: isSmallChat ? 4 : 16,
                                    }}
                                >
                                    <Typography
                                        variant='body2'
                                        sx={{
                                            color: 'primary.contrastText',
                                            textShadow: `-2px -2px 10px ${alpha(theme.palette.dark.main, 0.6)}, 2px 2px 10px ${alpha(theme.palette.dark.main, 0.6)}`,
                                            fontWeight: billingExcluded
                                                ? 'normal'
                                                : isSmallChat
                                                  ? '700'
                                                  : '600',
                                            fontSize: isSmallChat
                                                ? '9px'
                                                : '14px',
                                        }}
                                    >
                                        {billingExcluded
                                            ? 'Free'
                                            : minPrice === 0 && maxPrice === 0
                                              ? ''
                                              : `From ${formatCurrency(
                                                    minPrice,
                                                )}${
                                                    unitType
                                                        ? ` / ${formatUnitType(
                                                              unitType,
                                                          )}`
                                                        : ''
                                                }`}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </CardActionArea>
            </Card>
        </Box>
    );
};

export default ExperienceCard;
